/**
 * PhotoReviewList — phase 7 ("Sharing cycle") review & edit surface.
 *
 * This is the "edit later" screen. Teachers arrive here either during class
 * (alongside the sharing-cycle prompts above) or later, when they resume a
 * session using their code. Every edit autosaves through the useTeachingSession
 * hook.
 *
 * For each photo:
 *   - Editable caption textarea + voice-note recorder. The recorder
 *     transcribes via the same /api/speech-to-text the Name-with-Animals
 *     game uses, then appends the transcript into the caption field. The
 *     caption itself is the source of truth, so anything captured by voice
 *     can be edited later as plain text.
 *   - Student avatar chips (multi-select, from session roster).
 */

import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Camera, ChevronDown, ChevronUp, Mic, Square, Trash2, Loader2 } from 'lucide-react';
import type {
  PhotoWithUrl,
  TeachingPhotoRow,
  SkillTag,
  StudentAvatarRecord,
} from '../../lib/teachingSession';

interface Props {
  language: 'en' | 'es';
  photos: PhotoWithUrl[];
  avatars: StudentAvatarRecord[];
  onUpdatePhoto: (
    photoId: string,
    patch: {
      caption?: string;
      student_tags?: number[];
      skill_tags?: SkillTag[];
    },
  ) => void;
  onDelete: (photo: TeachingPhotoRow) => Promise<void>;
  /** Jump back to the capture phase to add more photos. */
  onJumpToCapture?: () => void;
}

export function PhotoReviewList({
  language,
  photos,
  avatars,
  onUpdatePhoto,
  onDelete,
  onJumpToCapture,
}: Props) {
  const t = (en: string, es: string) => (language === 'en' ? en : es);
  const [expandedId, setExpandedId] = useState<string | null>(
    photos.length > 0 ? photos[0].id : null,
  );

  // ── Voice-note recording state ────────────────────────────────────────
  // Only one recording can be in flight at a time. We tie the state to the
  // photo id so if the teacher collapses the panel mid-record (or expands a
  // different photo) we can stop and clean up cleanly.
  const [recordingPhotoId, setRecordingPhotoId] = useState<string | null>(null);
  const [transcribingPhotoId, setTranscribingPhotoId] = useState<string | null>(null);
  const [recordError, setRecordError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  /** Release the mic stream tracks. Always call when a recording session ends. */
  const releaseStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
  };

  // Cleanup on unmount — don't leak the mic if the component goes away
  // mid-record (e.g. teacher navigates away from the in-session phase).
  useEffect(() => {
    return () => {
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
      } catch {
        /* ignore */
      }
      releaseStream();
    };
  }, []);

  const transcribeBlob = async (
    blob: Blob,
  ): Promise<{ text: string } | { moderated: true } | null> => {
    const arrayBuffer = await blob.arrayBuffer();
    const base64 = btoa(
      new Uint8Array(arrayBuffer).reduce(
        (s, b) => s + String.fromCharCode(b),
        '',
      ),
    );
    const res = await fetch('/api/speech-to-text', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ audio: base64, language }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.details || `API error ${res.status}`);
    }
    const data = await res.json();
    if (data.moderated) return { moderated: true };
    const text = (data.text || '').trim();
    return text ? { text } : null;
  };

  const startRecording = async (photoId: string) => {
    setRecordError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };
      recorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        releaseStream();
        setRecordingPhotoId(null);
        setTranscribingPhotoId(photoId);
        try {
          const result = await transcribeBlob(audioBlob);
          if (result && 'moderated' in result) {
            setRecordError(
              t(
                'That note couldn’t be transcribed safely. Try again or type it instead.',
                'No se pudo transcribir esa nota de forma segura. Intenta de nuevo o escríbela.',
              ),
            );
            return;
          }
          if (!result) {
            setRecordError(
              t(
                'I didn’t catch that — try recording again or type your note.',
                'No entendí — intenta grabar de nuevo o escribe tu nota.',
              ),
            );
            return;
          }

          // Find the photo's current caption and append the transcript.
          const current = photos.find((p) => p.id === photoId);
          const existing = (current?.caption ?? '').trim();
          const merged = existing
            ? `${existing}\n${result.text}`.trim()
            : result.text;
          onUpdatePhoto(photoId, { caption: merged });
        } catch {
          setRecordError(
            t(
              'Couldn’t transcribe that note. Try again in a moment.',
              'No se pudo transcribir esa nota. Intenta de nuevo en un momento.',
            ),
          );
        } finally {
          setTranscribingPhotoId((cur) => (cur === photoId ? null : cur));
        }
      };

      recorder.start();
      setRecordingPhotoId(photoId);
    } catch {
      setRecordError(
        t(
          'Microphone access was blocked. Allow microphone permissions to record.',
          'Se bloqueó el acceso al micrófono. Permite el micrófono para grabar.',
        ),
      );
      releaseStream();
    }
  };

  const stopRecording = () => {
    try {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === 'recording'
      ) {
        mediaRecorderRef.current.stop();
      }
    } catch {
      /* ignore */
    }
  };

  if (photos.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl p-6 shadow-xl mb-4 text-center"
      >
        <p className="text-gray-500 mb-4">
          {t(
            'No photos yet. Jump back to capture moments from class.',
            'Aún no hay fotos. Vuelve a la captura para registrar momentos de la clase.',
          )}
        </p>
        {onJumpToCapture && (
          <button
            type="button"
            onClick={onJumpToCapture}
            className="inline-flex items-center gap-2"
            style={{
              padding: '12px 26px',
              background: '#42a57a',
              color: '#ffffff',
              border: 'none',
              borderRadius: 999,
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 6px 22px rgba(66,165,122,0.28)',
            }}
          >
            <Camera className="w-5 h-5" />
            {t('Take photos', 'Tomar fotos')}
          </button>
        )}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-3xl p-4 md:p-6 shadow-xl mb-4"
    >
      {/* CTA-style header — invites the teacher to act, calls out voice
          notes as an available shortcut, and surfaces "Take more photos"
          as a clear secondary action. */}
      <div
        className="mb-5 p-5 md:p-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4"
        style={{
          background: '#f4efde',
          border: '1px solid rgba(201,169,97,0.45)',
          borderRadius: 18,
        }}
      >
        <div className="min-w-0 flex-1">
          <p
            style={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#9a9a9a',
              marginBottom: 6,
            }}
          >
            {t('Your turn', 'Tu turno')}
          </p>
          <h3
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontStyle: 'italic',
              fontWeight: 500,
              fontSize: 'clamp(20px, 2.6vw, 26px)',
              color: '#2a6d5b',
              letterSpacing: '-0.005em',
              lineHeight: 1.2,
              marginBottom: 8,
            }}
          >
            {t('Tag a photo and add a note.', 'Etiqueta una foto y agrega una nota.')}
          </h3>
          <p
            className="inline-flex items-center gap-1.5"
            style={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: 13,
              fontWeight: 500,
              color: '#5f6e66',
              lineHeight: 1.5,
            }}
          >
            <Mic className="w-3.5 h-3.5" style={{ color: '#42a57a' }} aria-hidden />
            {t(
              'Tap the mic on any photo to dictate a voice note — it transcribes into the caption you can edit.',
              'Toca el micrófono en cualquier foto para dictar una nota de voz — se transcribe en la descripción y puedes editarla.',
            )}
          </p>
        </div>
        {onJumpToCapture && (
          <button
            type="button"
            onClick={onJumpToCapture}
            className="inline-flex items-center justify-center gap-1.5 flex-shrink-0 self-start sm:self-end"
            style={{
              padding: '10px 20px',
              background: '#ffffff',
              color: '#42a57a',
              border: '1.5px solid #42a57a',
              borderRadius: 999,
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <Camera className="w-4 h-4" />
            {t('Take more photos', 'Tomar más fotos')}
          </button>
        )}
      </div>

      <div className="space-y-3">
        {photos.map((photo) => {
          const expanded = expandedId === photo.id;
          const status =
            (photo.caption && photo.caption.trim()) ||
            photo.student_tags.length;
          const isRecording = recordingPhotoId === photo.id;
          const isTranscribing = transcribingPhotoId === photo.id;
          const recordingDisabled =
            (recordingPhotoId !== null && recordingPhotoId !== photo.id) ||
            (transcribingPhotoId !== null && transcribingPhotoId !== photo.id);

          return (
            <div
              key={photo.id}
              className="border-2 border-gray-100 rounded-2xl overflow-hidden hover:border-gray-200 transition-colors"
            >
              <button
                type="button"
                onClick={() => {
                  // If we're collapsing the panel that's recording, stop
                  // the recording first so the mic doesn't stay live.
                  if (expanded && isRecording) stopRecording();
                  setExpandedId(expanded ? null : photo.id);
                }}
                className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50"
              >
                <img
                  src={photo.publicUrl}
                  alt={photo.caption || 'Student work'}
                  className="w-16 h-16 md:w-20 md:h-20 object-cover rounded-lg flex-shrink-0 bg-gray-200"
                  loading="lazy"
                />
                <div className="flex-1 min-w-0">
                  <p
                    className={`text-sm ${
                      photo.caption && photo.caption.trim()
                        ? 'text-gray-800'
                        : 'text-gray-400 italic'
                    }`}
                  >
                    {photo.caption?.trim() || t('No caption yet', 'Sin descripción aún')}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {photo.student_tags.map((idx) => (
                      <span key={idx} className="text-base" aria-hidden>
                        {avatars[idx]?.emoji ?? '•'}
                      </span>
                    ))}
                    {!status && (
                      <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                        {t('Needs review', 'Por revisar')}
                      </span>
                    )}
                  </div>
                </div>
                {expanded ? (
                  <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                )}
              </button>

              {expanded && (
                <div className="p-3 md:p-4 border-t border-gray-100 space-y-4">
                  {/* Caption + voice note recorder */}
                  <div>
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <label className="block text-sm font-semibold text-gray-700">
                        {t('Caption', 'Descripción')}
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          if (isRecording) stopRecording();
                          else if (!recordingDisabled && !isTranscribing) {
                            void startRecording(photo.id);
                          }
                        }}
                        disabled={recordingDisabled || isTranscribing}
                        aria-pressed={isRecording}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full transition-colors"
                        style={{
                          padding: '6px 14px',
                          fontFamily: "'Inter', system-ui, sans-serif",
                          background: isRecording
                            ? '#b85a55'
                            : isTranscribing
                              ? 'transparent'
                              : 'transparent',
                          color: isRecording
                            ? '#ffffff'
                            : isTranscribing
                              ? '#9a9a9a'
                              : '#42a57a',
                          border: isRecording
                            ? '1.5px solid #b85a55'
                            : isTranscribing
                              ? '1.5px solid #d4d4d4'
                              : '1.5px solid #42a57a',
                          cursor:
                            recordingDisabled || isTranscribing
                              ? 'not-allowed'
                              : 'pointer',
                          opacity: recordingDisabled ? 0.5 : 1,
                        }}
                        title={
                          isRecording
                            ? t('Stop recording', 'Detener grabación')
                            : isTranscribing
                              ? t('Transcribing…', 'Transcribiendo…')
                              : t('Add a voice note', 'Agregar nota de voz')
                        }
                      >
                        {isTranscribing ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            {t('Transcribing…', 'Transcribiendo…')}
                          </>
                        ) : isRecording ? (
                          <>
                            <span
                              aria-hidden
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: '#ffffff',
                                animation: 'wp-rec-pulse 1s ease-in-out infinite',
                              }}
                            />
                            <Square className="w-3 h-3" fill="currentColor" />
                            {t('Stop', 'Detener')}
                          </>
                        ) : (
                          <>
                            <Mic className="w-3.5 h-3.5" />
                            {t('Voice note', 'Nota de voz')}
                          </>
                        )}
                      </button>
                    </div>
                    <textarea
                      value={photo.caption}
                      onChange={(e) =>
                        onUpdatePhoto(photo.id, { caption: e.target.value })
                      }
                      rows={3}
                      placeholder={t(
                        'Type or record a note about this moment…',
                        'Escribe o graba una nota sobre este momento…',
                      )}
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#42a57a]"
                    />
                    {isRecording && (
                      <p
                        className="mt-2 text-xs italic"
                        style={{ color: '#b85a55', fontFamily: "'Inter', system-ui, sans-serif" }}
                      >
                        {t(
                          'Listening… tap Stop when you’re done.',
                          'Escuchando… toca Detener cuando termines.',
                        )}
                      </p>
                    )}
                    {recordError && !isRecording && !isTranscribing && (
                      <p
                        className="mt-2 text-xs"
                        style={{ color: '#b85a55', fontFamily: "'Inter', system-ui, sans-serif" }}
                      >
                        {recordError}
                      </p>
                    )}
                  </div>

                  {/* Student tags */}
                  {avatars.length > 0 && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('Students in this photo', 'Estudiantes en esta foto')}
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {avatars.map((avatar, idx) => {
                          const selected = photo.student_tags.includes(idx);
                          return (
                            <button
                              type="button"
                              key={idx}
                              onClick={() => {
                                const nextTags = selected
                                  ? photo.student_tags.filter((i) => i !== idx)
                                  : [...photo.student_tags, idx];
                                onUpdatePhoto(photo.id, { student_tags: nextTags });
                              }}
                              aria-pressed={selected}
                              className={`inline-flex items-center gap-1 text-sm px-2.5 py-1.5 rounded-full border transition-colors ${
                                selected
                                  ? 'bg-[#e6f4ec] border-[#42a57a] text-[#2a6d5b]'
                                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              <span className="text-lg">{avatar.emoji}</span>
                              {avatar.name && (
                                <span className="text-xs font-medium">{avatar.name}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="pt-2 border-t border-gray-100 flex justify-end">
                    <button
                      type="button"
                      onClick={() => onDelete(photo)}
                      className="text-xs text-red-600 hover:text-red-700 font-semibold flex items-center gap-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      {t('Delete photo', 'Eliminar foto')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes wp-rec-pulse {
          0%, 100% { opacity: 0.4; transform: scale(0.85); }
          50%      { opacity: 1;   transform: scale(1.1); }
        }
      `}</style>
    </motion.div>
  );
}
